import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaCalendar, FaClock, FaSearch, FaCarSide, FaStar } from "react-icons/fa";

const UpcomingToursSection: React.FC = () => {
  // State for travel finder
  const [destination, setDestination] = useState<string>("all");
  const [duration, setDuration] = useState<string>("all");
  const [departureDate, setDepartureDate] = useState<string>("");

  // Tour data from rioninfooforsite.md
  const allTours = [
    {
      id: 1,
      imageSrc: "/Home/NambiaHighlights.jpeg",
      location: "Namibia",
      title: "Namibia Highlights Safari",
      price: "€3,600",
      duration: "14 days",
      highlights: "UNESCO Sossusvlei dunes, Coastal exploration in Swakopmund, Desert elephants at Twyfelfontein, Abundant wildlife in Etosha National Park",
      route: "/tours/namibia-highlights",
      rating: 5,
      nextDeparture: "2025-04-10"
    },
    {
      id: 2,
      imageSrc: "/Home/Bigfivee.jpeg",
      location: "Namibia & Botswana",
      title: "Big Five Safari (Nambovic)",
      price: "€4,800",
      duration: "19 days",
      highlights: "Track all Big Five animals, Experience Etosha's wildlife, Explore Chobe River ecosystems, Visit Sossusvlei and Swakopmund",
      route: "/tours/big-five-safari",
      rating: 5,
      nextDeparture: "2025-05-15"
    },
    {
      id: 3,
      imageSrc: "/Home/Namwildsafari.jpeg",
      location: "Namibia",
      title: "Namwild Safari",
      price: "€1,900",
      duration: "9 days",
      highlights: "Coastal wildlife in Swakopmund, Explore Etosha South and North regions, Visit Monte Etjo Safari Lodge",
      route: "/tours/namwild",
      rating: 4.8,
      nextDeparture: "2025-06-01"
    },
    {
      id: 4,
      imageSrc: "/Home/Victoria.jpeg",
      location: "Namibia, Botswana, Zimbabwe",
      title: "Victoria Falls Adventure",
      price: "€4,500",
      duration: "18 days",
      highlights: "Experience the magnificent Victoria Falls, Explore the Okavango Delta ecosystem, Cruise the wildlife-rich Chobe River",
      route: "/tours/victoria-falls",
      rating: 4.9,
      nextDeparture: "2025-07-10"
    },
    {
      id: 5,
      imageSrc: "/Home/WalvisExcursions.jpeg",
      location: "Namibia",
      title: "Walvisbay Excursions",
      price: "€80",
      duration: "6+ hours",
      highlights: "Visit the stunning Pink Lake, Climb massive Dune 7, See ancient Welwitschia plants, Photograph the unique Moonlandscape",
      route: "/tours/walvisbay",
      rating: 4.7,
      nextDeparture: "Daily"
    }
  ];

  // Filter tours based on selected criteria
  const filteredTours = allTours.filter(tour => {
    // Filter by destination
    if (destination !== "all" && !tour.location.toLowerCase().includes(destination.toLowerCase())) {
      return false;
    }

    // Filter by duration
    if (duration !== "all") {
      const days = parseInt(tour.duration);
      if (isNaN(days)) {
        // Handle special cases like "6+ hours"
        return duration === "short";
      }

      switch (duration) {
        case "short":
          return days <= 10;
        case "medium":
          return days > 10 && days <= 15;
        case "long":
          return days > 15;
        default:
          return true;
      }
    }

    // Filter by departure date
    if (departureDate && tour.nextDeparture !== "Daily") {
      const selectedDate = new Date(departureDate);
      const tourDate = new Date(tour.nextDeparture);
      if (selectedDate > tourDate) {
        return false;
      }
    }

    return true;
  });

  return (
    <section className="w-full mt-[81px] px-4 sm:px-6 md:px-8 py-[5px] max-md:mt-16">
      <div className="max-w-[1200px] mx-auto">
        {/* Section Header */}
        <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
          <div>
            <div className="subheading">
              <img
                src="https://cdn.builder.io/api/v1/image/assets/d65dcfbabd9245a5ac68b0712fd94192/6c0d89f877af1dcc6732f857c943f6e250e8b1d1986a6a99370e80a012207347?placeholderIfAbsent=true"
                alt="Icon"
                className="aspect-[1] object-contain w-[31px] fill-[#A86747] shrink-0"
              />
              <span>Let's Explore Together</span>
            </div>
            <h2 className="heading-primary text-[#3D3E48] mt-1">
              Upcoming Tours
            </h2>
          </div>

          <Link
            to="/tours"
            className="text-[#C05B2A] text-base font-bold leading-[29px] flex items-center gap-2 hover:underline group"
          >
            See All Tours
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transform transition-transform group-hover:translate-x-1"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </Link>
        </div>

        {/* Enhanced Travel Finder Bar */}
        <div className="bg-white shadow-lg rounded-xl p-6 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMapMarkerAlt className="text-[#C05B2A]" />
                </div>
                <select
                  className="block w-full pl-10 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-[#C05B2A] focus:border-[#C05B2A] rounded-lg shadow-sm"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                >
                  <option value="all">All Destinations</option>
                  <option value="namibia">Namibia</option>
                  <option value="botswana">Botswana</option>
                  <option value="zimbabwe">Zimbabwe</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaClock className="text-[#C05B2A]" />
                </div>
                <select
                  className="block w-full pl-10 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-[#C05B2A] focus:border-[#C05B2A] rounded-lg shadow-sm"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                >
                  <option value="all">Any Duration</option>
                  <option value="short">Short (1-10 days)</option>
                  <option value="medium">Medium (11-15 days)</option>
                  <option value="long">Long (16+ days)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Departure After</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaCalendar className="text-[#C05B2A]" />
                </div>
                <input
                  type="date"
                  className="block w-full pl-10 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-[#C05B2A] focus:border-[#C05B2A] rounded-lg shadow-sm"
                  value={departureDate}
                  onChange={(e) => setDepartureDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="flex items-end">
              <button
                className="w-full bg-[#C05B2A] text-white py-3 px-6 rounded-lg text-base font-bold hover:bg-[#A04B1A] transition-all duration-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
              >
                <FaSearch className="text-sm" />
                <span>Find Tours</span>
              </button>
            </div>
          </div>
        </div>

        {/* Improved Tour Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTours.map((tour) => (
            <Link
              to={tour.route}
              key={tour.id}
              className="group block transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl rounded-xl overflow-hidden"
            >
              <div className="bg-white rounded-xl overflow-hidden shadow-md h-full flex flex-col">
                {/* Image Section */}
                <div className="relative h-52 md:h-60 overflow-hidden">
                  <img
                    src={tour.imageSrc}
                    alt={tour.title}
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>

                  {/* Location and Duration Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center text-sm">
                        <FaMapMarkerAlt className="mr-1.5 text-[#C05B2A]" />
                        <span className="font-medium">{tour.location}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <FaClock className="mr-1.5 text-[#C05B2A]" />
                        <span>{tour.duration}</span>
                      </div>
                    </div>
                  </div>

                  {/* Rating Stars */}
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-full py-1 px-2.5 flex items-center gap-1">
                    <FaStar className="text-amber-400 w-3.5 h-3.5" />
                    <span className="text-gray-800 text-sm font-medium">{tour.rating}</span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#C05B2A] transition-colors">
                    {tour.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {tour.highlights}
                  </p>

                  {/* Guide and Price */}
                  <div className="mt-auto">
                    <div className="flex items-center text-[#C05B2A] mb-2">
                      <FaCarSide className="mr-2" />
                      <span className="text-sm font-medium">With Professor Rion Haraeb</span>
                    </div>
                    <div className="flex items-baseline">
                      <span className="text-xl font-bold text-gray-900">{tour.price}</span>
                      <span className="text-sm text-gray-600 ml-1">per person</span>
                    </div>

                    {/* Next Departure */}
                    <div className="mt-2 text-xs text-gray-500">
                      Next departure: {tour.nextDeparture === "Daily" ? "Available daily" : new Date(tour.nextDeparture).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* No Results Message */}
        {filteredTours.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">No tours match your search criteria</div>
            <button
              onClick={() => {
                setDestination("all");
                setDuration("all");
                setDepartureDate("");
              }}
              className="text-[#C05B2A] font-medium hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default UpcomingToursSection;
